import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { PenTool, RotateCcw, Home, CheckCircle, Clock, FileText, AlertTriangle, Sparkles, Loader2, Award, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import html2pdf from 'html2pdf.js';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ExpressionEcriteResults() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const userName = user?.nom || user?.username || 'Candidat';
  const {
    blocName = 'Expression Écrite',
    blocId,
    taches = [],
    responses = {},
    wordCounts = {},
    timeSpent = 0,
    completedCount = 0
  } = location.state || {};

  const [aiResults, setAiResults] = useState({});
  const [isCorrecting, setIsCorrecting] = useState({});
  const hasAutoStarted = useRef(false);
  const creditUsedRef = useRef(false); // 1 credit for the whole session (all 3 taches)

  const formatScore = (score) => Math.floor(Number(score || 0));

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) return `${mins} min ${secs} sec`;
    return `${secs} sec`;
  };

  const getTacheStatus = (index) => {
    const wc = wordCounts[index] || 0;
    const tache = taches[index];
    const hasResponse = responses[index]?.trim().length > 0;

    if (!hasResponse) return 'empty';
    if (wc >= tache.minMots && wc <= tache.maxMots) return 'valid';
    if (wc < tache.minMots) return 'under';
    return 'over';
  };

  const validCount = taches.filter((_, i) => getTacheStatus(i) === 'valid').length;

  const handleCorrectWithAI = async (index) => {
    const tache = taches[index];
    const response = responses[index] || '';

    if (!response.trim()) return;

    // Block correction immediately if user has no credits
    const currentUser = JSON.parse(localStorage.getItem('tcf-current-user') || '{}');
    const isSessionStart = !creditUsedRef.current;
    if (isSessionStart && (currentUser.credits_ia ?? 0) <= 0) {
      toast.error("Nombre d'essais épuisé !", {
        description: "Veuillez contacter votre centre pour ajouter des essais.",
        action: {
          label: "WhatsApp",
          onClick: () => window.open(`https://wa.me/23057235683`, '_blank')
        },
        duration: 10000
      });
      return;
    }

    // First tache with content charges 1 credit for the whole session
    if (isSessionStart) creditUsedRef.current = true;

    setIsCorrecting(prev => ({ ...prev, [index]: true }));

    try {
      const res = await fetch(`${API_URL}/api/ai/correct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          module: 'expression_ecrite',
          tache_number: tache.tache,
          sujet: tache.sujet,
          instruction: tache.instruction,
          user_response: response,
          min_mots: tache.minMots || 0,
          max_mots: tache.maxMots || 0,
          user_id: currentUser.id,
          is_session_start: isSessionStart
        })
      });

      const data = await res.json();
      if (res.status === 403) {
        toast.error("Nombre d'essais épuisé !", {
          description: "Veuillez contacter votre centre pour ajouter des essais.",
          action: {
            label: "WhatsApp",
            onClick: () => window.open(`https://wa.me/23057235683`, '_blank')
          },
          duration: 10000
        });
        return;
      }
      if (!res.ok) throw new Error(data.detail || 'Erreur');

      setAiResults(prev => ({ ...prev, [index]: data }));
    } catch (error) {
      console.error(error);
      toast.error("Erreur de correction");
    } finally {
      setIsCorrecting(prev => ({ ...prev, [index]: false }));
    }
  };

  // Auto-start correction for all tasks on load
  useEffect(() => {
    if (hasAutoStarted.current) return;
    hasAutoStarted.current = true;

    taches.forEach((_, index) => {
      if (responses[index]?.trim()) {
        handleCorrectWithAI(index);
      }
    });
  }, [taches, responses]);

  // Calculate Global Score (average of available AI scores)
  const aiScores = Object.values(aiResults).map(r => r.score);
  const globalScore = aiScores.length > 0 ? (aiScores.reduce((a, b) => a + b, 0) / aiScores.length).toFixed(1) : null;

  // Grille TCF Canada Expression Écrite
  const getGlobalLevel = (s) => {
    if (s >= 18) return { cecrl: "C2 NIVEAU AVANCÉ", nclc: "12" };
    if (s >= 16) return { cecrl: "C1 FORT", nclc: "10" };
    if (s >= 14) return { cecrl: "C1", nclc: "9" };
    if (s >= 12) return { cecrl: "B2 FORT", nclc: "8" };
    if (s >= 10) return { cecrl: "B2", nclc: "7" };
    if (s >= 8)  return { cecrl: "B1 FORT", nclc: "6" };
    if (s >= 6)  return { cecrl: "B1", nclc: "5" };
    if (s >= 4)  return { cecrl: "A2 FORT", nclc: "4" };
    if (s >= 2)  return { cecrl: "A2", nclc: "3" };
    return { cecrl: "A1 NON ATTEINT", nclc: "1" };
  };
  const globalLevel = globalScore ? getGlobalLevel(parseFloat(globalScore)) : null;

  const formatText = (text) => {
    if (!text) return <p className="text-muted-foreground italic">(Aucune réponse rédigée)</p>;

    const cleaned = text
      .replace(/\r\n/g, '\n')
      .replace(/^["“]|["”]$/g, '')
      .trim();

    const paragraphs = cleaned.split(/\n+/);

    return paragraphs.map((paragraph, i) => (
      <p key={i} className="whitespace-pre-wrap mb-3 last:mb-0">
        {paragraph.trim()}
      </p>
    ));
  };

  const formatTextForPdf = (text, emptyLabel = '(Aucune réponse rédigée)') => {
    const safeText = String(text || '').trim();
    if (!safeText) {
      return `<p class="pdf-empty">${escapeHtml(emptyLabel)}</p>`;
    }

    const cleaned = safeText
      .replace(/\r\n/g, '\n')
      .replace(/^["“]|["”]$/g, '')
      .trim();

    const paragraphs = cleaned.split(/\n+/);

    return paragraphs
      .map((paragraph) => `<p class="pdf-paragraph">${escapeHtml(paragraph.trim())}</p>`)
      .join('');
  };

  const formatFeedbackForPdf = (text) => {
    const safeText = String(text || '').trim();
    if (!safeText) {
      return '<p class="pdf-empty">Aucun feedback disponible.</p>';
    }

    const formatted = safeText
      .replace(/^SCORE:.*\n?/gi, '')
      .replace(/^CORRECTION:.*\n?/gi, '')
      .replace(/^FEEDBACK:.*\n?/gi, '')
      .trim();

    const sections = formatted
      .split(/(?=\d+\.\s+\*\*.*?\*\*)/g)
      .map((section) => section.trim())
      .filter(Boolean);

    if (sections.length === 0) {
      return `<p class="pdf-paragraph">${escapeHtml(formatted).replace(/\n/g, '<br>')}</p>`;
    }

    return sections.map((section) => {
      const headerMatch = section.match(/^(\d+\.\s+\*\*.*?\*\*)\s*:?\s*(.*)$/s);
      if (!headerMatch) {
        return `<p class="pdf-paragraph">${escapeHtml(section).replace(/\n/g, '<br>')}</p>`;
      }

      const title = escapeHtml(headerMatch[1].replace(/\*\*/g, ''));
      const body = headerMatch[2].trim();
      const bodyLines = body
        .split(/\n+/)
        .map((line) => line.trim())
        .filter(Boolean);

      return `<div class="pdf-feedback-block"><p class="pdf-feedback-title">${title}</p>${bodyLines.length > 0 ? bodyLines.map((line) => `<p class="pdf-paragraph">${escapeHtml(line).replace(/\n/g, '<br>')}</p>`).join('') : ''}</div>`;
    }).join('');
  };

  const handleDownloadPdf = () => {
    const date = new Date().toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });

    const tasksHtml = taches.map((tache, index) => {
      const wc = wordCounts[index] || 0;
      const response = responses[index] || '';
      const result = aiResults[index];
      const score = result ? formatScore(result.score) : null;
      const nclcLevel = result?.cecrl_level || result?.nclc_level || 'En attente';
      const feedbackHtml = result ? formatFeedbackForPdf(result.feedback) : '<p class="pdf-empty">Correction IA en attente.</p>';
      const correctedTextHtml = result ? formatTextForPdf(result.corrected_text, 'Aucune correction proposée.') : '<p class="pdf-empty">Correction IA en attente.</p>';

      return `
        <section class="task-card">
          <div class="task-card-header">
            <div class="task-badge">Tâche ${escapeHtml(tache.tache)}</div>
            <div class="score-pill">${score !== null ? `${score}/20` : 'En attente'}</div>
          </div>

          <div class="task-summary">
            <div><span>Mot(s)</span><strong>${wc}</strong></div>
            <div><span>Limite</span><strong>${escapeHtml(`${tache.minMots || 0}-${tache.maxMots || 0}`)}</strong></div>
            <div><span>NCLC</span><strong>${escapeHtml(nclcLevel)}</strong></div>
          </div>

          <div class="section">
            <div class="section-title">Sujet</div>
            <div class="section-box section-box-blue">${escapeHtml(tache.sujet || 'Sujet non disponible.').replace(/\n/g, '<br>')}</div>
          </div>

          <div class="section">
            <div class="section-title">Votre réponse</div>
            <div class="section-box section-box-gray">${formatTextForPdf(response)}</div>
          </div>

          <div class="section">
            <div class="section-title">Correction suggérée</div>
            <div class="section-box section-box-green">${correctedTextHtml}</div>
          </div>

          <div class="section">
            <div class="section-title">Analyse & Feedback</div>
            <div class="section-box section-box-white">${feedbackHtml}</div>
          </div>
        </div>
      `;
    }).join('');

    const html = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8" />
  <title>Résultats Expression Écrite - ${escapeHtml(blocName)}</title>
  <style>
    * { box-sizing: border-box; }
    .pdf-wrapper {
      margin: 0;
      padding: 24px;
      font-family: Arial, Helvetica, sans-serif;
      color: #1a1a1a;
      background: #fff;
      max-width: 800px;
      margin: 0 auto;
    }
    .header {
      background: linear-gradient(135deg, #6d28d9, #4f46e5);
      color: #fff;
      border-radius: 20px;
      padding: 24px;
      margin-bottom: 18px;
    }
    .header-top {
      display: flex;
      justify-content: space-between;
      gap: 16px;
      flex-wrap: wrap;
      align-items: flex-start;
    }
    .header h1 {
      margin: 0 0 6px 0;
      font-size: 24px;
      line-height: 1.2;
    }
    .header p {
      margin: 0;
      color: rgba(255,255,255,0.9);
      line-height: 1.5;
    }
    .summary {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 12px;
      margin: 18px 0 24px;
    }
    .summary-card {
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 16px;
      padding: 14px 16px;
      box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
    }
    .summary-card .label {
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: #64748b;
      margin-bottom: 8px;
      font-weight: 700;
    }
    .summary-card .value {
      font-size: 22px;
      font-weight: 800;
      color: #1e293b;
    }
    .task-card {
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 20px;
      padding: 20px;
      margin-bottom: 18px;
    }
    .task-card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      margin-bottom: 14px;
    }
    .task-badge {
      display: inline-block;
      background: #ede9fe;
      color: #6d28d9;
      font-size: 12px;
      font-weight: 700;
      padding: 4px 10px;
      border-radius: 999px;
    }
    .score-pill {
      flex-shrink: 0;
      background: #eff6ff;
      color: #1d4ed8;
      border: 1px solid #bfdbfe;
      border-radius: 999px;
      padding: 8px 14px;
      font-size: 14px;
      font-weight: 800;
    }
    .task-summary {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 12px;
      margin-bottom: 16px;
    }
    .task-summary div {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 14px;
      padding: 12px 14px;
    }
    .task-summary span {
      display: block;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: #64748b;
      margin-bottom: 4px;
      font-weight: 700;
    }
    .task-summary strong {
      font-size: 16px;
      color: #0f172a;
    }
    .section { margin-top: 14px; }
    .section-title {
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: #6b7280;
      margin-bottom: 8px;
      font-weight: 800;
    }
    .section-box {
      border-radius: 14px;
      border: 1px solid #e2e8f0;
      padding: 14px 16px;
      line-height: 1.6;
      font-size: 13px;
      color: #1f2937;
      white-space: pre-wrap;
    }
    .section-box-blue { background: #eff6ff; border-left: 4px solid #2563eb; }
    .section-box-gray { background: #f8fafc; }
    .section-box-green { background: #ecfdf5; border-left: 4px solid #22c55e; }
    .section-box-white { background: #fff; white-space: normal; }
    .pdf-paragraph { margin: 0 0 6px 0; }
    .pdf-paragraph:last-child { margin-bottom: 0; }
    .pdf-empty { color: #94a3b8; font-style: italic; margin: 0; }
    .pdf-feedback-block { margin-bottom: 10px; }
    .pdf-feedback-block:last-child { margin-bottom: 0; }
    .pdf-feedback-title {
      margin: 0 0 3px 0;
      font-weight: 800;
      font-size: 12px;
      color: #4f46e5;
    }
    .footer {
      margin-top: 22px;
      text-align: center;
      color: #94a3b8;
      font-size: 11px;
      padding-top: 12px;
      border-top: 1px solid #e2e8f0;
    }
    @media print {
      body { background: #fff; padding: 0; }
      .header, .summary-card, .task-card, .section-box { box-shadow: none !important; }
      .task-card { margin-bottom: 14px; }
      .task-card + .task-card { page-break-before: always; break-before: always; }
      .task-card-header { break-inside: avoid; page-break-inside: avoid; }
      .task-summary { break-inside: avoid; page-break-inside: avoid; }
      .section { break-inside: avoid; page-break-inside: avoid; }
      .section-box { break-inside: avoid; page-break-inside: avoid; }
      .pdf-feedback-block { break-inside: avoid; page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="header">
      <div class="header-top">
        <div>
          <h1>Résultats d'Expression Écrite</h1>
          <p><strong>Candidat :</strong> ${escapeHtml(userName)}</p>
          <p>${escapeHtml(blocName)}</p>
          <p>Date : ${escapeHtml(date)}</p>
          <p><strong>Résultat complet :</strong> correction IA, note et analyse par tâche incluses</p>
        </div>
        <div>
          <p><strong>Temps total :</strong> ${escapeHtml(formatTime(timeSpent))}</p>
          <p><strong>Réponses complétées :</strong> ${escapeHtml(String(completedCount))}/${escapeHtml(String(taches.length))}</p>
          ${globalScore ? `<p><strong>Note globale IA :</strong> ${escapeHtml(formatScore(globalScore))}/20</p>` : ''}
        </div>
      </div>
    </div>

    <div class="summary">
      <div class="summary-card">
        <div class="label">Score total /20</div>
        <div class="value">${globalScore ? `${escapeHtml(formatScore(globalScore))}` : 'N/A'}</div>
      </div>
      <div class="summary-card">
        <div class="label">Tâches corrigées</div>
        <div class="value">${escapeHtml(String(Object.keys(aiResults).length))}/${escapeHtml(String(taches.length))}</div>
      </div>
      <div class="summary-card">
        <div class="label">Niveau CECRL</div>
        <div class="value">${globalLevel ? escapeHtml(globalLevel.cecrl) : 'N/A'}</div>
      </div>
      <div class="summary-card">
        <div class="label">Niveau NCLC</div>
        <div class="value">${globalLevel ? escapeHtml(globalLevel.nclc) : 'N/A'}</div>
      </div>
    </div>

    <div style="margin: 0 0 22px 0; background: #fff7ed; border: 1px solid #fed7aa; border-radius: 16px; padding: 14px 16px; color: #9a3412;">
      <strong>Résumé complet :</strong> la note globale, la correction suggérée, la réponse initiale et l’analyse détaillée sont affichées pour chaque tâche.
    </div>

    ${tasksHtml}

    <div class="footer">TCF Canada - Export PDF des résultats</div>
  </div>
</body>
</html>`;

    const element = document.createElement('div');
    element.innerHTML = html;

    const opt = {
      margin:       10,
      filename:     `resultats-expression-ecrite-${(userName || 'Candidat').replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.pdf`,
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  { scale: 2, useCORS: true },
      jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    toast.info('Génération du PDF en cours...', { duration: 3000 });
    html2pdf().from(element).set(opt).save().then(() => {
      toast.success('PDF téléchargé avec succès !');
    });
  };

  const formatFeedback = (text) => {
    if (!text) return null;
    const formatted = text
      .replace(/^SCORE:.*\n?/gi, '')
      .replace(/^CORRECTION:.*\n?/gi, '')
      .replace(/^FEEDBACK:.*\n?/gi, '')
      .trim();

    const sections = formatted.split(/(?=\d+\.\s+\*\*.*?\*\*)/g).map(section => section.trim()).filter(Boolean);

    return sections.map((section, index) => {
      const headerMatch = section.match(/^(\d+\.\s+\*\*.*?\*\*)\s*:?\s*(.*)$/s);
      if (!headerMatch) {
        return (
          <p key={index} className="mb-3 last:mb-0 leading-relaxed whitespace-pre-wrap">
            {section}
          </p>
        );
      }

      const title = headerMatch[1].replace(/\*\*/g, '');
      const body = headerMatch[2].trim();
      const bodyLines = body
        .split(/\n+/)
        .map(line => line.trim())
        .filter(Boolean);

      return (
        <div key={index} className="space-y-2">
          <p className="font-bold text-indigo-700 dark:text-indigo-400 mt-3 mb-1">{title}</p>
          {bodyLines.length > 0 ? bodyLines.map((line, i) => (
            <p key={i} className="leading-relaxed whitespace-pre-wrap">
              {line}
            </p>
          )) : null}
        </div>
      );
    });
  };

  const handleRetry = () => {
    navigate(`/modules/expression-ecrite/exercise?bloc=${blocId}`);
  };

  const handleBackToBlocs = () => {
    navigate('/modules/expression-ecrite');
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6 animate-in pb-8 px-4">
        {/* Header Banner */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 rounded-2xl text-center shadow-lg">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Résultats d'Expression Écrite</h1>
          <p className="text-purple-100 opacity-90">{blocName}</p>
        </div>

        {/* Global Evaluation Card */}
        {globalScore && (
          <div className="bg-white dark:bg-gray-800 border-2 border-purple-500 rounded-2xl p-6 shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <Award className="h-24 w-24" />
            </div>
            <div className="text-center space-y-4">
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">Évaluation Globale IA</h2>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="bg-purple-50 dark:bg-purple-900/30 px-6 py-3 rounded-2xl border border-purple-100 dark:border-purple-800">
                  <p className="text-xs text-purple-600 dark:text-purple-400 font-bold mb-1">NOTE SUR 20</p>
                  <p className="text-3xl font-black text-purple-700 dark:text-purple-300">{formatScore(globalScore)}</p>
                </div>
                <div className="bg-indigo-50 dark:bg-indigo-900/30 px-6 py-3 rounded-2xl border border-indigo-100 dark:border-indigo-800">
                  <p className="text-xs text-indigo-600 dark:text-indigo-400 font-bold mb-1">NIVEAU</p>
                  <p className="text-2xl font-black text-indigo-700 dark:text-indigo-300 leading-tight">{globalLevel.cecrl}</p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/30 px-6 py-3 rounded-2xl border border-blue-100 dark:border-blue-800">
                  <p className="text-xs text-blue-600 dark:text-blue-400 font-bold mb-1">NCLC</p>
                  <p className="text-3xl font-black text-blue-700 dark:text-blue-300">{globalLevel.nclc}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <button onClick={handleRetry} className="flex items-center justify-center space-x-2 py-4 rounded-xl bg-purple-500 text-white hover:bg-purple-600 font-semibold transition-all hover:scale-[1.02] active:scale-[0.98] shadow-md">
            <RotateCcw className="h-5 w-5" />
            <span>Refaire l'examen</span>
          </button>
          <button onClick={handleDownloadPdf} className="flex items-center justify-center space-x-2 py-4 rounded-xl bg-slate-900 text-white hover:bg-slate-800 font-semibold transition-all hover:scale-[1.02] active:scale-[0.98] shadow-md">
            <FileDown className="h-5 w-5" />
            <span>Télécharger le PDF</span>
          </button>
          <button onClick={handleBackToBlocs} className="flex items-center justify-center space-x-2 py-4 rounded-xl border-2 border-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 font-semibold transition-all shadow-sm">
            <Home className="h-5 w-5" />
            <span>Retour aux blocs</span>
          </button>
        </div>

        {/* Detailed Review */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold">Détails par tâche</h3>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>{validCount}/{taches.length} conformes</span>
            </div>
          </div>

          {taches.map((tache, index) => {
            const status = getTacheStatus(index);
            const wc = wordCounts[index] || 0;
            const response = responses[index] || '';
            const result = aiResults[index];

            return (
              <div key={index} className={`bg-white dark:bg-gray-800 rounded-2xl border-2 overflow-hidden transition-all ${status === 'valid' ? 'border-green-200 dark:border-green-900/50' : 'border-orange-200 dark:border-orange-900/50'
                }`}>
                <div className={`px-5 py-4 flex flex-wrap items-center justify-between gap-4 ${status === 'valid' ? 'bg-green-50/50 dark:bg-green-900/10' : 'bg-orange-50/50 dark:bg-orange-900/10'
                  }`}>
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 text-white flex items-center justify-center font-bold shadow-sm">
                      {tache.tache}
                    </div>
                    <div>
                      <h4 className="font-bold">Tâche {tache.tache}</h4>
                      <p className="text-xs text-muted-foreground">{wc} mots • {tache.minMots}-{tache.maxMots} requis</p>
                    </div>
                  </div>

                  {isCorrecting[index] ? (
                    <div className="flex items-center space-x-2 px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-600 rounded-lg animate-pulse">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-xs font-bold uppercase">Correction IA en cours...</span>
                    </div>
                  ) : result ? (
                    <div className="flex items-center space-x-2 flex-wrap gap-1">
                      <div className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg text-xs font-bold border border-green-200 dark:border-green-800">
                        {formatScore(result.score)}/20
                      </div>
                      <div className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-lg text-xs font-bold border border-indigo-200 dark:border-indigo-800">
                        {result.cecrl_level}
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => handleCorrectWithAI(index)} className="text-xs font-bold text-purple-600 hover:underline">
                      Réessayer la correction
                    </button>
                  )}
                </div>

                <div className="p-5 space-y-4">
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-xl border border-indigo-100 dark:border-indigo-800">
                    <p className="text-xs font-bold text-indigo-600 dark:text-indigo-300 uppercase mb-2">Sujet de la tâche :</p>
                    <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{tache.sujet || "Sujet non disponible."}</p>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-900/40 p-4 rounded-xl border border-gray-100 dark:border-gray-800">
                    <p className="text-xs font-bold text-gray-400 uppercase mb-2">Votre réponse :</p>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{response || "Aucune réponse fournie."}</p>
                  </div>

                  {result && (
                    <div className="space-y-4 animate-in slide-in-from-bottom-2">
                      <div>
                        <p className="text-xs font-bold text-indigo-500 uppercase mb-2 flex items-center space-x-1">
                          <Sparkles className="h-3 w-3" />
                          <span>Correction suggérée :</span>
                        </p>
                        <div className="bg-green-50/50 dark:bg-green-900/10 p-4 rounded-xl border border-green-100 dark:border-green-900/30 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                          <div className="space-y-3 italic">
                            {formatText(result.corrected_text)}
                          </div>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-purple-500 uppercase mb-2">Analyse & Feedback :</p>
                        <div className="bg-white dark:bg-gray-700/50 p-4 rounded-xl border border-gray-100 dark:border-gray-700 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                          {formatFeedback(result.feedback)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
